import React, { useEffect } from 'react';
import { FEATURE_DELETE_SHORTCUT } from '../flags';
import { deleteActiveCodeField } from '../state/actions/delete-active-code-field';
import { deleteItems } from '../state/actions/delete-items';
import {
  deleteTransitions,
  getSelectedTransitionIds,
  getSelectedNonTransitionIds,
} from '../state/actions/delete-transition';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';
import {
  useLinkingEnabled,
  useCurrentStateAsRef,
  useSelectedItems,
  useTracks,
  useWriteContext,
} from '../utils/use-context';

export const BackspaceToDelete: React.FC = () => {
  const { selectedItems } = useSelectedItems();
  const { setState } = useWriteContext();
  const { tracks } = useTracks();
  const isLinkingEnabled = useLinkingEnabled();
  const stateAsRef = useCurrentStateAsRef();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEventTargetInputElement(e)) {
        return;
      }

      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        FEATURE_DELETE_SHORTCUT &&
        selectedItems.length > 0
      ) {
        const codeFieldDeleteResult = deleteActiveCodeField(
          stateAsRef.current,
          stateAsRef.current.selectedItems,
        );

        if (codeFieldDeleteResult) {
          if (codeFieldDeleteResult.state === stateAsRef.current) {
            return;
          }

          setState({
            update: codeFieldDeleteResult.state,
            commitToUndoStack: codeFieldDeleteResult.commitToUndoStack,
          });
          return;
        }

        setState({
          update: (state) => {
            // 트랜지션 ID와 일반 아이템 ID 분리
            const transitionIds = getSelectedTransitionIds(state);
            const nonTransitionIds = getSelectedNonTransitionIds(state);

            // 삭제 가능한 일반 아이템만 필터링 (잠긴 트랙 제외)
            const deletableItems = nonTransitionIds.filter((itemId) => {
              const track = tracks.find((t) => t.items.includes(itemId));
              return !track?.locked;
            });

            // 아무것도 삭제할 게 없으면 상태 유지
            if (transitionIds.length === 0 && deletableItems.length === 0) {
              return state;
            }

            // 먼저 트랜지션 삭제
            let newState = state;
            if (transitionIds.length > 0) {
              newState = deleteTransitions(newState, transitionIds);
            }

            // 그 다음 일반 아이템 삭제
            if (deletableItems.length > 0) {
              newState = deleteItems(newState, deletableItems, {
                isLinkingEnabled,
              });
            }

            return newState;
          },
          commitToUndoStack: true,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLinkingEnabled, selectedItems, setState, stateAsRef, tracks]);

  return null;
};
